import { useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../services/supabaseClient';

export interface ExportEligibility {
  can_export: boolean;
  daily_limit: number | null;
  tier: string;
  reason?: string;
  message: string;
  last_export_date?: string;
}

export function useExportEligibility() {
  const { user } = useAuth();
  const [isChecking, setIsChecking] = useState(false);

  /**
   * Check if user is eligible to export flows today
   */
  const checkEligibility = useCallback(async (): Promise<ExportEligibility> => {
    if (!user) {
      return {
        can_export: false,
        daily_limit: 0,
        tier: 'none',
        message: 'User not authenticated'
      };
    }

    setIsChecking(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      if (!token) {
        throw new Error('No access token');
      }

      const response = await fetch('/api/subscription/check-export-eligibility', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to check export eligibility');
      }

      const data = await response.json();
      return data;

    } catch (error) {
      console.error('Error checking export eligibility:', error);
      return {
        can_export: false,
        daily_limit: 0,
        tier: 'error',
        message: error instanceof Error ? error.message : 'Failed to check eligibility'
      };
    } finally {
      setIsChecking(false);
    }
  }, [user]);

  /**
   * Record that user has performed an export (updates last_export_date)
   */
  const recordExport = useCallback(async (): Promise<void> => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

      // Update the account's last_export_date
      const { error } = await supabase
        .from('accounts')
        .update({ last_export_date: today })
        .eq('owner_id', user.id);

      if (error) {
        console.error('Error recording export:', error);
        throw new Error('Failed to record export');
      }

      console.log('[EXPORT-ELIGIBILITY] Recorded export for today:', today);

    } catch (error) {
      console.error('Error in recordExport:', error);
      throw error;
    }
  }, [user]);

  return {
    checkEligibility,
    recordExport,
    isChecking
  };
}
